// Step 1 of the booking flow — initial lead capture screen.
//
// Two layout variants (modal | inline) × two phone flavours (optional |
// mandatory) give the four widget forms:
//
//   modal  + optional   → production modal: Name / Email / Studio (no phone).
//   modal  + mandatory  → same modal, plus a required Phone field + SMS opt-in.
//   inline + optional   → "START YOUR FITTING" card, 3 fields stacked in ONE
//                         column (no phone).
//   inline + mandatory  → same card, 4 fields in 2 columns (adds Phone) + SMS
//                         opt-in.
//
// In the mandatory flavours the phone is required here and the SMS-consent
// checkbox is collected here too (it moves off the last Get Set Up step).
//
// The international markets are on the OPTIONAL flavour here since 2026-10-10:
// their phone is required on the Confirm step instead (see
// CountryConfig.phoneOnConfirmStep and steps/ConfirmAppointment.tsx). The
// field and the SMS consent are shared components so both screens render the
// same control.

import { useCallback, useEffect, useState } from 'react';
import styles from '../styles/widget.module.css';
import type { BookingAction, BookingState } from '../state/machine';
import { AlertMessage } from '../components/AlertMessage';
import { LocationField } from '../components/LocationField';
import { FittingTypeField } from '../components/FittingTypeField';
import { FitterField } from '../components/FitterField';
import { fittingDisplayFor, pricingLabelFor } from '../data/fittingTypes';
import { promoPricingLines } from '../data/promoPricing';
import { resolveWireName } from '../data/fittingNames';
import { getNextAvailableSummary } from '../api/client';
import { formatPillDate, formatSlotTime } from '../utils/datetime';
import { phoneComplete } from '../utils/phone';
import { PhoneField } from '../components/PhoneField';
import { SmsOptIn } from '../components/SmsOptIn';
import { countryConfigOrDefault } from '../config/countries';
import swingerLogo from '../assets/images/swinger-logo.png';

// Each market publishes its own policy; linking an Australian customer to the
// US one misstates what they are consenting to.
const PrivacyLink = ({
  country,
  children = 'Privacy Policy',
}: {
  country?: string | null;
  children?: React.ReactNode;
}) => (
  <a
    href={countryConfigOrDefault(country).privacyPolicyUrl}
    target="_blank"
    rel="noopener noreferrer"
    className={styles.classicGreenLink}
  >
    {children}
  </a>
);


interface LeadCaptureProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  onContinue: () => void;
  showExpiredNotice?: boolean;
  /**
   * 'modal' (default) = the classic centered layout (logo + heading + wide
   * field row) used by the button-triggered embed.
   * 'inline' = the landing-page card: a "START YOUR FITTING" header, then the
   * lead fields and a left-aligned BOOK MY FITTING button.
   * 'card' = the hero seed card: START YOUR BOOKING kicker, Name/Email,
   * labelled YOUR STUDIO / FITTING TYPE rows and a live NEXT AVAILABLE strip.
   */
  variant?: 'modal' | 'inline' | 'card';
  /** Heading for the 'card' variant. Defaults to "Book a fitting near you". */
  cardHeading?: string;
  cardWide?: boolean;
  cardBasic?: boolean;
  /**
   * When true the Phone field is shown here and required, and the SMS-consent
   * opt-in is collected here (both flavours). When false (default) the phone is
   * collected later on the Get Set Up step — the production flow.
   */
  phoneMandatory?: boolean;
  /**
   * Add a FITTER row to the seed card (card variant only) — the location/fitter
   * context's fifth Step-1 field. A fitter CTA preselects it; the visitor can
   * still change it or fall back to "Any Fitter". Off by default.
   */
  showFitter?: boolean;
  /**
   * Show the "Need to move an existing appointment? Reschedule" entry under
   * the CTA. Off by default since 2026-10-19: the only approved way into the
   * reschedule flow is the manage-appointment page, whose inline mount boots
   * straight into it (data-cc-mode="reschedule"), so the link is suppressed
   * on every other mount in every market (Dan Parks: not ready for AU, CA,
   * UK). The flow itself is untouched; only the way in is hidden.
   */
  allowReschedule?: boolean;
}

export function LeadCapture({
  state,
  dispatch,
  onContinue,
  showExpiredNotice,
  variant = 'modal',
  cardHeading,
  cardWide,
  cardBasic,
  phoneMandatory = false,
  showFitter = false,
  allowReschedule = false,
}: LeadCaptureProps) {
  const inline = variant === 'inline';
  const card = variant === 'card';

  // ── Seed-card state (hooks stay unconditional; only the card reads them) ──
  // Whether the FITTING TYPE row is showing its Change dropdown.
  const [changingFitting, setChangingFitting] = useState(false);
  // The NEXT AVAILABLE strip: null = nothing to show (no studio yet, fetch
  // failed, or genuinely no openings — all render as no strip rather than a
  // guess), 'loading' while a fetch is out.
  const [nextAvail, setNextAvail] = useState<
    'loading' | { firstStart: string; weekSlotCount: number } | null
  >(null);
  const cardLocationId = card ? state.selectedLocation?.id : undefined;
  const cardFittingName = card ? state.selectedFittingType?.name : undefined;
  const cardFittingDuration = card ? state.selectedFittingType?.durationMinutes : undefined;
  useEffect(() => {
    if (!card || !state.selectedLocation) {
      setNextAvail(null);

      return;
    }
    let stale = false;
    setNextAvail('loading');
    getNextAvailableSummary({
      location: state.selectedLocation,
      durationMin: cardFittingDuration ?? 60,
      fittingType: cardFittingName,
    })
      .then((summary) => {
        if (!stale) setNextAvail(summary);
      })
      .catch(() => {
        // A failed lookup is a missing strip, never a made-up time.
        if (!stale) setNextAvail(null);
      });

    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card, cardLocationId, cardFittingName, cardFittingDuration]);
  // Reschedule mode — entered via the link under the CTA. Step 1 becomes a
  // name+email identity lookup: the studio field is inert (it comes from the
  // looked-up appointment), phone is never required, and the CTA reads
  // "Reschedule" and drives GET /bookings/lookup instead of the lead path
  // (widget.tsx's onContinue branches on the same flag).
  const reschedule = state.rescheduleMode;
  // The SMS consent copy is US TCPA language — autodialer, "Msg & data rates
  // apply". Markets that do not send SMS drop the checkbox entirely rather
  // than ask for consent to something that never happens.
  const { smsOptInOffered, consent, requiredConsent, consentCentered } =
    countryConfigOrDefault(state.country);

  // Per-field "touched" tracking drives the subtle red hairline: a required
  // field only shows the invalid border once the user has interacted with it
  // (blur), or once they hit the CTA on an invalid form (marks all at once).
  // A pristine, untouched form shows no red. Border-only — no text.
  type FieldName = 'name' | 'email' | 'studio' | 'phone' | 'privacy';
  const [touched, setTouched] = useState<Record<FieldName, boolean>>({
    name: false,
    email: false,
    studio: false,
    phone: false,
    privacy: false,
  });
  const markTouched = (f: FieldName) =>
    setTouched((t) => (t[f] ? t : { ...t, [f]: true }));
  const markAllTouched = () =>
    setTouched({ name: true, email: true, studio: true, phone: true, privacy: true });

  // Per-field validity (same signals the CTA gate has always used).
  const nameValid = state.fullName.trim().length > 1;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email.trim());
  const studioValid = state.selectedLocation != null;
  const phoneValid = phoneComplete(state.phone, state.country, state.dialCodeOverride);
  // Markets without a required consent are unconditionally valid here, so the
  // gate cannot accidentally block a market that was never asked to have one.
  const privacyValid = !requiredConsent || state.privacyAccepted;

  // Phone is required only in the mandatory flavours (where it lives here).
  // Reschedule lookup verifies identity by name + email alone — the studio
  // comes from the appointment on file, the phone is never required (even in
  // the mandatory flavour), and no marketing consent is being collected.
  const canContinue = reschedule
    ? nameValid && emailValid
    : nameValid && emailValid && studioValid && (!phoneMandatory || phoneValid) && privacyValid;


  // A field lights red only when it's been touched AND is invalid. Studio and
  // phone are not asked for in reschedule mode, so they never show error state.
  const nameError = touched.name && !nameValid;
  const emailError = touched.email && !emailValid;
  const studioError = !reschedule && touched.studio && !studioValid;
  const phoneError = !reschedule && phoneMandatory && touched.phone && !phoneValid;
  const privacyError = !reschedule && touched.privacy && !privacyValid;

  // Stable handler so the <form>'s onSubmit prop identity doesn't change
  // every render. Browsers' autofill heuristics index the form on first
  // paint; if React were re-keying anything underneath a churn-y handler
  // the heuristics could miss the fields.
  //
  // The CTA is intentionally NOT disabled (see the buttons below): clicking an
  // invalid form must fire so we can mark every required field touched at once,
  // lighting up all the missing ones — this is what fixes the silent
  // "the button did nothing" trap, textlessly.
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (canContinue) {
        onContinue();
      } else {
        markAllTouched();
      }
    },
    [canContinue, onContinue],
  );

  const nameField = (
    <div className={styles.field}>
      <label className={styles.label} htmlFor="cc-fullName">Name*</label>
      <input
        id="cc-fullName"
        name="name"
        className={styles.input}
        type="text"
        autoComplete="name"
        autoCorrect="off"
        spellCheck={false}
        value={state.fullName}
        placeholder="What's your full name?"
        aria-invalid={nameError}
        onBlur={() => markTouched('name')}
        onChange={(e) => dispatch({ type: 'SET_LEAD', payload: { fullName: e.target.value } })}
      />
    </div>
  );

  const emailField = (
    <div className={styles.field}>
      <label className={styles.label} htmlFor="cc-email">Email*</label>
      <input
        id="cc-email"
        name="email"
        className={styles.input}
        type="email"
        autoComplete="email"
        inputMode="email"
        autoCorrect="off"
        spellCheck={false}
        value={state.email}
        placeholder="What is your email?"
        aria-invalid={emailError}
        onBlur={() => markTouched('email')}
        onChange={(e) => dispatch({ type: 'SET_LEAD', payload: { email: e.target.value } })}
      />
    </div>
  );

  const phoneField = (
    <PhoneField
      state={state}
      dispatch={dispatch}
      invalid={phoneError}
      onTouched={() => markTouched('phone')}
      // Inline hides the labels, so the placeholder describes the field like
      // the others; modal keeps its label, so it shows the format hint.
      placeholder={inline ? "What's your phone number?" : undefined}
    />
  );

  const emailOptIn = (
    <label className={styles.checkboxRow}>
      <input
        type="checkbox"
        checked={state.promoOptIn}
        onChange={(e) => dispatch({ type: 'SET_LEAD', payload: { promoOptIn: e.target.checked } })}
      />
      <span>
        {consent ? (
          // Label and body are separate blocks rather than one run split by a
          // <br>. A forced break makes the browser skip `text-wrap: balance`,
          // which is what leaves these sentences setting a full line and then
          // a stub. The label is optional — the UK is one plain sentence.
          <>
            {consent.emailLabel && (
              <strong className={styles.consentOptInLabel}>{consent.emailLabel}</strong>
            )}
            <span className={styles.consentOptInBody}>{consent.emailBody}</span>
          </>
        ) : (
          <>
            By checking the box, you consent to receive marketing email messages
            from Club Champion. Please visit our <PrivacyLink country={state.country} /> for more information.
          </>
        )}
      </span>
    </label>
  );

  // Heading + footnote only exist where a market supplies them. AU groups its
  // two consents under a heading and closes with a footnote; the UK rewords one
  // sentence and wants neither, so both are absent rather than empty.
  const consentHeading = consent?.heading ? (
    <p className={styles.consentHeading}>
      <strong>{consent.heading}</strong>
      {consent.intro ? <> — {consent.intro}</> : null}
    </p>
  ) : null;

  const consentFootnote = consent?.footnotePrefix ? (
    <p className={styles.consentFootnote}>
      {consent.footnotePrefix}
      <PrivacyLink country={state.country} />
      {consent.footnoteSuffix}
    </p>
  ) : null;

  // The required consent — the UK privacy policy. Unticked, blocks Continue,
  // and deliberately dispatches nothing that leaves the browser: CC asked for
  // it not to be recorded, so it lives in local state and is read only by the
  // gate above.
  const privacyOptIn = requiredConsent ? (
    <div className={styles.leadModalOptin}>
      <label className={styles.checkboxRow} data-invalid={privacyError || undefined}>
        <input
          type="checkbox"
          checked={state.privacyAccepted}
          aria-invalid={privacyError}
          aria-describedby={privacyError ? 'cc-privacy-error' : undefined}
          onChange={(e) => {
            markTouched('privacy');
            dispatch({ type: 'SET_LEAD', payload: { privacyAccepted: e.target.checked } });
          }}
        />
        <span>
          <span className={styles.consentOptInBody}>
            {requiredConsent.bodyPrefix}
            <PrivacyLink country={state.country}>{requiredConsent.linkText ?? 'Privacy Policy'}</PrivacyLink>
            {requiredConsent.bodySuffix}
          </span>
        </span>
      </label>
      {/* Unlike every other field here this one gets words, not just a red
          hairline. A checkbox border is too quiet to explain why a button that
          looks enabled did nothing — which is the exact trap the CTA below is
          written to avoid. */}
      {privacyError && (
        <p className={styles.consentRequiredError} id="cc-privacy-error" role="alert">
          {requiredConsent.error}
        </p>
      )}
    </div>
  ) : null;

  // SMS-consent opt-in — mandatory flavours only. Sits beneath the phone,
  // styled like the email opt-in. Shared with the Confirm step; the value is
  // postBookingSmsOptIn so the last-step submit persists it unchanged.
  const smsOptIn = <SmsOptIn state={state} dispatch={dispatch} />;

  // ── Hero seed-card variant ──────────────────────────────────────────────
  // The compact in-hero panel: same state, same validation, same submit as
  // every other Step 1 — a different arrangement of it, not a different step.
  if (card) {
    const ft = state.selectedFittingType;
    const ftDisplay = ft ? fittingDisplayFor(ft, state.country) : null;
    // The seed card's price text, the same way the fitting grid prints it: the
    // Salesforce-resolved price for the chosen studio first, else the authored
    // label for the market under the current promotion. Reading the base
    // `pricingLabel` here printed the pre-promotion US wording ("FREE w/ custom
    // club purchase…") beside a grid that said $100 (Annie, 2026-10-19).
    const seedPriceText = (() => {
      if (!ft) return '';
      const wireName = resolveWireName(ft.name, state.fittingNameMap);
      const lines = promoPricingLines(state.promoPricing?.[wireName], true, state.country);
      if (lines) {
        const l1 = `${lines.line1.bold}${lines.line1.rest}`.trim();
        const l2 = `${lines.line2.bold}${lines.line2.rest}`.trim();
        return l2 ? `${l1}; ${l2}` : l1;
      }
      return pricingLabelFor(ft, state.country);
    })();
    const slot = nextAvail !== 'loading' && nextAvail ? nextAvail : null;

    // The card promised a NEXT AVAILABLE time, so continuing carries it into
    // the flow: the date is selected outright and the exact start is left as
    // a pending pick for TimeSlots to confirm against the list it loads —
    // if the opening was taken in the meantime, the flow lands on the date
    // with the picker open rather than asserting a slot that no longer
    // exists.
    const handleCardSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!canContinue) {
        markAllTouched();

        return;
      }
      if (slot) {
        dispatch({ type: 'SELECT_DATE', payload: slot.firstStart.slice(0, 10) });
        dispatch({ type: 'SET_PENDING_SLOT', payload: slot.firstStart });
      }
      onContinue();
    };

    return (
      <div className={`${styles.leadCapture} ${styles.seedCard} ${cardWide ? styles.seedCardWide : ''}`}>
        {showExpiredNotice && (
          <AlertMessage
            type="warning"
            title="Your session has expired"
            description="The allotted time to confirm your appointment has elapsed. Please try again."
            className={styles.appointmentExpiredAlert}
          />
        )}

        <p className={styles.seedKicker}>Start your booking</p>
        <h2 className={styles.seedTitle}>{cardHeading ?? 'Book a fitting near you'}</h2>

        <form
          className={`${styles.leadFields} ${cardWide ? styles.seedFormWide : ''}`}
          method="post"
          action="javascript:void(0)"
          autoComplete="on"
          onSubmit={handleCardSubmit}
        >
          <input
            className={styles.seedInput}
            name="name"
            type="text"
            autoComplete="name"
            autoCorrect="off"
            spellCheck={false}
            value={state.fullName}
            placeholder="Full Name*"
            aria-label="Full name"
            aria-invalid={nameError}
            onBlur={() => markTouched('name')}
            onChange={(e) => dispatch({ type: 'SET_LEAD', payload: { fullName: e.target.value } })}
          />
          <input
            className={styles.seedInput}
            name="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoCorrect="off"
            spellCheck={false}
            value={state.email}
            placeholder="Email*"
            aria-label="Email"
            aria-invalid={emailError}
            onBlur={() => markTouched('email')}
            onChange={(e) => dispatch({ type: 'SET_LEAD', payload: { email: e.target.value } })}
          />

            {/* Wrappers are ALWAYS rendered and are `display: contents` unless the
                card is wide, so the single-column card's box tree — and therefore
                every existing mount, US and international — is unchanged. */}
            <div className={styles.seedColContext}>
          <p className={styles.seedLabel}>Your studio</p>
          <div className={styles.seedStudioField}>
            <LocationField
              state={state}
              dispatch={dispatch}
              dropdownPlacement="bottom"
              invalid={studioError}
              onBlur={() => markTouched('studio')}
            />
          </div>

          {
            // Always offered: with no selection (the preset stood down after
            // a studio change, or none was configured) the picker renders in
            // place of the row, so the fitting is never un-set-able.
          }
          {/* cardBasic drops this row: with no fitting context the question
              lands before the visitor has picked a studio, so the fitting is
              chosen later in the flow instead. */}
          {!cardBasic && (ft || changingFitting || state.selectedLocation) && (
            <>
              <p className={styles.seedLabel}>Fitting type</p>
              {changingFitting || !ft || !ftDisplay ? (
                <div
                  className={styles.seedStudioField}
                >
                  {/* autoOpen: the row's Change just cleared the selection, so
                      landing on a closed "Select Fitting Type" trigger reads
                      as the click doing nothing. onPick restores the row. */}
                  {/* autoOpen only when the customer just clicked Change —
                      a selection that emptied by itself (preset stood down)
                      shows the closed trigger rather than popping a list
                      nobody asked for. */}
                  <FittingTypeField
                    state={state}
                    dispatch={dispatch}
                    autoOpen={changingFitting}
                    onPick={() => setChangingFitting(false)}
                  />
                </div>
              ) : (
                <div className={styles.seedRow}>
                  <div>
                    <p className={styles.seedRowName}>{ftDisplay.displayName}</p>
                    <p className={styles.seedRowSub}>
                      {ftDisplay.durationLabel} &middot; {seedPriceText}
                    </p>
                  </div>
                  <button
                    type="button"
                    className={styles.seedChange}
                    onClick={() => {
                      // Clear first: FittingTypeField only renders its picker
                      // when nothing is selected. The preset effect will not
                      // fight this — it re-applies per location, not per null.
                      setChangingFitting(true);
                      dispatch({ type: 'SELECT_FITTING_TYPE', payload: null });
                    }}
                  >
                    Change
                  </button>
                </div>
              )}
            </>
          )}

          {/* FITTER — the location/fitter context's fifth field. Needs a studio
              first (the resource list is studio-scoped), so it appears once one
              is chosen; a fitter CTA preselects it, and "Any Fitter" is offered
              as the default. */}
          {showFitter && state.selectedLocation && (
            <>
              <p className={styles.seedLabel}>Fitter</p>
              <div className={styles.seedStudioField}>
                <FitterField state={state} dispatch={dispatch} />
              </div>
            </>
          )}

            </div>

            <div className={styles.seedColOutcome}>
          {slot && (
            <>
              <p className={styles.seedLabel}>Next available</p>
              <div className={`${styles.seedRow} ${styles.seedRowSlot}`}>
                <div>
                  <p className={styles.seedRowName}>
                    {/* House formatters, deliberately: slot timestamps carry
                        the store's wall clock behind a fake Z, so a Date
                        round-trip shifts them by the browser's offset — the
                        first render showed a 9 AM opening as 2 AM. */}
                    {formatPillDate(slot.firstStart.slice(0, 10))}
                    {' '}&middot;{' '}
                    {formatSlotTime(slot.firstStart)}
                  </p>
                  <p className={styles.seedRowSub}>{state.selectedLocation?.name}</p>
                </div>
                {slot.weekSlotCount > 1 && (
                  <p className={styles.seedRowMore}>{slot.weekSlotCount - 1} more this week</p>
                )}
              </div>
            </>
          )}

          {consentHeading}
          {emailOptIn}
          {privacyOptIn}
          {consentFootnote}

          <button
            type="submit"
            className={`${styles.button} ${styles.seedCta}`}
          >
            Continue &middot; Book your fitting
          </button>
          <p className={styles.seedFinePrint}>No credit card needed to book. Reschedule any time.</p>
            </div>
        </form>
      </div>
    );
  }
  // Lookup failure — 404 "no appointment found" and network errors both land
  // here, rendered with the same AlertMessage treatment as the session-expired
  // notice above the form.
  const rescheduleLookupAlert =
    reschedule && state.rescheduleLookupError ? (
      <AlertMessage
        type="warning"
        title="We couldn’t find your appointment"
        description={state.rescheduleLookupError}
        className={styles.appointmentExpiredAlert}
      />
    ) : null;

  // Link under the CTA — enters reschedule mode; once in it, swaps for a way
  // back to the normal booking flow. Rendered only where the mount allows it
  // (see allowReschedule) or when already in the mode, so the way back stays.
  const rescheduleToggle = !(allowReschedule || reschedule) ? null : (
    <p className={styles.rescheduleToggleRow}>
      {reschedule ? (
        <button
          type="button"
          className={styles.linkButton}
          data-cc-track="cta"
          data-cc-track-id="reschedule_exit"
          onClick={() => dispatch({ type: 'EXIT_RESCHEDULE_MODE' })}
        >
          Back to booking
        </button>
      ) : (
        <>
          Need to move an existing appointment?{' '}
          <button
            type="button"
            className={styles.linkButton}
            data-cc-track="cta"
            data-cc-track-id="reschedule_enter"
            onClick={() => dispatch({ type: 'ENTER_RESCHEDULE_MODE' })}
          >
            Reschedule
          </button>
        </>
      )}
    </p>
  );

  // Reschedule CTA content is shared by both variants: label swap + spinner
  // while the lookup is in flight.
  const rescheduleCtaLabel = state.rescheduleLookupSubmitting ? (
    <>
      <span className={styles.spinner} />
      Reschedule
    </>
  ) : (
    'Reschedule'
  );

  // ── Inline landing-page variant ─────────────────────────────────────────
  if (inline) {
    return (
      <div className={`${styles.leadCapture} ${styles.leadCaptureInline}`}>
        {showExpiredNotice && (
          <AlertMessage
            type="warning"
            title="Your session has expired"
            description="The allotted time to confirm your appointment has elapsed. Please try again."
            className={styles.appointmentExpiredAlert}
          />
        )}

        <div className={styles.leadInlineHeader}>
          <h2 className={styles.leadInlineTitle}>Start Your Fitting</h2>
          <p className={styles.leadInlineBadge}>
            Takes About 2 Minutes<br />No Credit Card Required
          </p>
        </div>
        <p className={styles.leadInlineStrap}>
          Tell us where and we’ll match you with a Master Fitter.
        </p>

        <form
          className={styles.leadFields}
          method="post"
          action="javascript:void(0)"
          autoComplete="on"
          onSubmit={handleSubmit}
        >
          {phoneMandatory ? (
            <>
              <div className={`${styles.leadFieldsRow} ${styles.leadFieldsRowInline}`}>
                {nameField}
                {emailField}
              </div>
              <div className={`${styles.leadFieldsRow} ${styles.leadFieldsRowInline}`}>
                {phoneField}
                <LocationField
                  state={state}
                  dispatch={dispatch}
                  dropdownPlacement="bottom"
                  invalid={studioError}
                  onBlur={() => markTouched('studio')}
                  disabled={reschedule}
                />
              </div>
            </>
          ) : (
            <div className={styles.leadFieldsColInline}>
              {nameField}
              {emailField}
              <LocationField
                state={state}
                dispatch={dispatch}
                dropdownPlacement="bottom"
                invalid={studioError}
                onBlur={() => markTouched('studio')}
                disabled={reschedule}
              />
            </div>
          )}

          {rescheduleLookupAlert}

          <button
            type="submit"
            className={`${styles.button} ${styles.leadCaptureContinueInline}`}
            disabled={reschedule && state.rescheduleLookupSubmitting}
            data-cc-track="cta"
            data-cc-track-id="lead_capture_continue"
          >
            {reschedule ? rescheduleCtaLabel : 'Book My Fitting'}
          </button>

          {rescheduleToggle}

          {consentHeading}
          <div className={`${styles.leadOptin} ${styles.leadOptinInline}`}>{emailOptIn}</div>
          {/* AU folds SMS into the single combined marketing box above, so the
              separate SMS checkbox is suppressed where consent.combined is set. */}
          {phoneMandatory && smsOptInOffered && !consent?.combined && (
            <div className={`${styles.leadOptin} ${styles.leadOptinInline}`}>{smsOptIn}</div>
          )}
          {privacyOptIn && (
            <div className={`${styles.leadOptin} ${styles.leadOptinInline}`}>{privacyOptIn}</div>
          )}
        </form>
      </div>
    );
  }

  // ── Modal variant ───────────────────────────────────────────────────────
  return (
    <div className={styles.leadCapture}>
      {showExpiredNotice && (
        <AlertMessage
          type="warning"
          title="Your session has expired"
          description="The allotted time to confirm your appointment has elapsed. Please try again."
          className={styles.appointmentExpiredAlert}
        />
      )}
      <div className={styles.stepHeader}>
        <img
          src={swingerLogo}
          alt="Club Champion"
          className={styles.leadCaptureLogo}
        />
        <h2 className={styles.heading}>Book Your Custom Fitting</h2>
        <p className={styles.leadCaptureCopy}>
          World’s #1 Custom Club Fitter
        </p>
      </div>
      {/* <form> wrapper is what lets Chrome and Safari confidently classify
          this as a contact form and offer autofill. method=post + a no-op
          action prevents accidental navigation if anything submits without
          our preventDefault. autoComplete=on on the parent reinforces the
          per-field tokens. */}
      <form
        className={styles.leadFields}
        method="post"
        action="javascript:void(0)"
        autoComplete="on"
        onSubmit={handleSubmit}
      >
        {/* Mandatory adds Phone as a 4th field (4-column row on desktop);
            optional keeps the production 3-column Name · Email · Studio row.
            LocationField is a shared component that owns its own autocomplete
            dropdown + pill-once-selected behavior. */}
        <div
          className={`${styles.leadFieldsRow} ${phoneMandatory ? styles.leadFieldsRowQuad : ''}`}
        >
          {nameField}
          {emailField}
          {phoneMandatory && phoneField}
          <LocationField
            state={state}
            dispatch={dispatch}
            invalid={studioError}
            onBlur={() => markTouched('studio')}
            disabled={reschedule}
          />
        </div>

        {/* Consent small print, aligned to field columns via the same grid.
            Mandatory: email + SMS stacked (email first) in one block spanning
            the Email + Phone columns. Optional: the single email consent under
            the Email field, at that field's width. Both rendered small. */}
        {phoneMandatory ? (
          <div className={`${styles.leadFieldsRow} ${styles.leadFieldsRowQuad} ${styles.leadConsentRow}`}>
            <div
              className={`${styles.leadConsentSpan} ${
                consentCentered ? styles.leadConsentSpanCentered : ''
              }`.trim()}
            >
              {consentHeading}
              <div className={styles.leadModalOptin}>{emailOptIn}</div>
              {/* AU folds SMS into the single combined marketing box above, so
                  the separate SMS checkbox is suppressed where consent.combined
                  is set. */}
              {smsOptInOffered && !consent?.combined && (
                <div className={styles.leadModalOptin}>{smsOptIn}</div>
              )}
              {privacyOptIn}
            </div>
            {/* Outside the consent block on purpose: it is a footnote to the
                whole step, so it spans the full field row and centers on the
                form. Inside the block it centered on the block, which sits a
                column to the right and so read as off-center. */}
            {consentFootnote}
          </div>
        ) : (
          <div className={`${styles.leadFieldsRow} ${styles.leadConsentRow}`}>
            <div className={`${styles.leadModalOptin} ${styles.leadConsentColEmail}`}>
              {emailOptIn}
              {privacyOptIn}
            </div>
          </div>
        )}

        {rescheduleLookupAlert}

        <button
          type="submit"
          className={`${styles.button} ${styles.stepButtonMobile} ${styles.leadCaptureContinue}`}
          disabled={reschedule && state.rescheduleLookupSubmitting}
          data-cc-track="cta"
          data-cc-track-id="lead_capture_continue"
        >
          {reschedule ? rescheduleCtaLabel : 'Continue'}
        </button>

        {rescheduleToggle}
      </form>
    </div>
  );
}
